<template>
	<Disclosure :title="$t('text.availabilityInfoTitle')" :error="sectionMessage.error" :warning="sectionMessage.warning" :message="sectionMessage.message"
		:lock="!userMayEditFields || productIsExternal" data-cy="availabilityInfo" ref="SECTION_availabilityInfo"
	>
		<IfFeatureFlag flag="FEATURE_TRADE_HUB">
			<Field v-if="hasField('requestOnly') && featureEnabled('trade-hub') && featureEnabled('trade-hub-additional-settings')"
				v-model="model.fields.requestOnly"
				typeName="ProductAvailability2"
				fieldName="requestOnly"
				@change="onRequestOnlyChanged"
			/>
		</IfFeatureFlag>

		<!-- MYS-7491: event tickets only show the remaining contingent below this threshold -->
		<div v-if="showContingentThreshold" class="contingentThreshold mb-6" data-cy="contingentThreshold">
			<v-label>{{ $t('text.contingentThreshold') }}</v-label>
			<p class="helpText" v-html="$t('text.contingentThresholdHelp')" />
			<!-- class Field: picks up the global .Field .input styling without the Field component's left border -->
			<NumberField class="Field" v-model="model.fields.contingentThreshold.de" :min="1" integer-only :field="contingentThresholdFieldDefinition" />
		</div>

		<div>
			<v-label>{{ $t('text.availabilityContingentTitle') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
			<p class="helpText" v-html="$t('text.availabilityContingentDesc')" />
		</div>

		<div v-if="hasField('productAvailabilities')" style="display: flex; flex-direction: column; gap: 10px; padding-top: 10px;">
			<div class="availability panel" v-for="(availability, availabilityIndex) in model.fields.productAvailabilities.de"
				:key="availability.sys.id"
			>
				<v-btn v-if="model.fields.productAvailabilities.de.length > 1"
					id="btnRemoveAvailabilityRow"
					class="removeRow"
					@click="removeAvailabilityRow(availabilityIndex)"
				>
					<v-icon color="white">mdi-delete</v-icon>
				</v-btn>
				<div v-if="csError && csError.data.availabilityId === availability.sys.id" class="error-container">
					<p id="errors" v-html="formatCSErrors"/>
				</div>
				<div>
					<div class="col2">
						<div>
							<DatePicker v-if="availability.fields.validFromDate"
								v-model="availability.fields.validFromDate.de"
								:errors="validFromToError(availability, 'validFromDate')"
								dataCy="validFromDate"
							>
								<template #label>{{ $t('text.validFrom') }} <span class="error-text">({{ $t('text.required') }})</span></template>
							</DatePicker>
						</div>
						<div>
							<DatePicker v-if="availability.fields.validToDate"
								v-model="availability.fields.validToDate.de"
								:min="availability.fields.validFromDate.de"
								:errors="validFromToError(availability, 'validToDate')"
								dataCy="validToDate"
							>
								<template #label>{{ $t('text.validUntil') }} <span class="error-text">({{ $t('text.required') }})</span></template>
							</DatePicker>
						</div>
					</div>

					<p class="helpText" v-html="$t('text.validDatesDesc')" v-if="availabilityIndex == 0" />
					<div :class="{ 'col2': !requestOnlyService }" class="mt-4" v-if="hasField('productContingents')">
						<div v-if="!requestOnlyService">
							<v-label>{{ $t('text.maxContingent') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
						</div>
						<div>
							<v-label>{{ $t('text.time') }}</v-label>
						</div>
					</div>
					<div v-if="hasField('productContingents')">
						<div v-for="(contingent, contingentIndex) in availability.fields.productContingents.de" :key="contingent.sys.id"
							class="contingent-row"
						>
							<div :class="{ 'col2': !requestOnlyService }">
								<div>
									<v-text-field variant="outlined" density="compact" hide-details
										v-if="!requestOnlyService"
										data-cy="maximumContingent"
										type="number"
										:min="contingent.fields.maximumContingent.de"
										v-model="contingent.fields.maximumContingent.de"
										:error-messages="contingentError(contingent)"
										style="background: white;"
										@focus="toggleContingentExpansion(availabilityIndex, contingentIndex)"
									/>
								</div>
								<div>
									<div style="display: flex; gap: 10px; align-items: flex-end;">
										<div style="flex-grow: 1;">
											<!-- show a red color around the field if the timeslotError length is bigger than 0 -->
											<v-text-field variant="outlined" density="compact" hide-details
												:error-messages="timeSlotError(contingent)"
												data-cy="timeSlot"
												v-mask="'##:##'"
												placeholder="00:00"
												v-model="contingent.fields.timeSlot.de"
												@blur="contingent.fields.timeSlot.de = fixTime(contingent.fields.timeSlot.de); "
												@focus="toggleContingentExpansion(availabilityIndex, contingentIndex)"
												style="background: white;"
											/>
										</div>
										<div style="max-width: 50px;">
											<v-btn size="small" style="height: 44px !important;" id="btnAddContingentRow" class="gradientButton" elevation="0" v-if="contingentIndex === 0"
												@click="addContingentRow(availabilityIndex)"
											>
												<v-icon size="24px">mdi-plus</v-icon>
											</v-btn>
											<v-btn size="small" style="height: 44px !important;" id="btnRemoveContingentRow" class="gradientButton" elevation="0" v-if="contingentIndex !== 0"
												@click="removeContingentRow(availabilityIndex, contingentIndex)"
											>
												<v-icon size="24px" color="#fb3640">mdi-delete</v-icon>
											</v-btn>
										</div>
									</div>
								</div>
							</div>

							<!-- Manual Contingents Warning -->
							 <IfFeatureFlag flag="FEATURE_MANUAL_CONTINGENTS_WARNING">
								<ManualContingentsWarning 
									v-if="isContingentExpanded(availabilityIndex, contingentIndex)"
									:manual-validities="getManualValiditiesForContingent(availability, contingent)"
									:expanded-key="`${availabilityIndex}-${contingentIndex}`"
									:space-id="model?.fields?.ticket?.de?.fields?.ticketOptions?.de?.[0]?.fields?.spaceId?.de"
									@hide="hideContingentExpansion(availabilityIndex, contingentIndex)"
									@navigate-to-date="navigateToDateInCalendar"
									@validity-deleted="onValidityDeleted"
								/>
							</IfFeatureFlag>
						</div>
					</div>
				</div>
				<p class="helpText" v-html="$t('text.contingentsHelp')" v-if="availabilityIndex == 0 && serviceWithTicket" />
				<p v-if="serviceWithTicket" class="helpText red-text" v-html="$t('text.timeslotHelp')" />

				<div>
					<AvailabilityExceptionSelector
						v-model="availability.fields.exceptions.de"
						:min="availability.fields.validFromDate.de"
						:max="availability.fields.validToDate.de"
						data-cy="exceptions"
					/>
				</div>

				<!--
				<p class="helpText mb-2" v-html="$t('text.additionalDateSettingsHelp')" v-if="availabilityIndex == 0" />
				-->

				<!--
				<v-btn id="btnRemoveAvailabilityRow" class="gradientButton" elevation="0"
					@click="removeAvailabilityRow(availabilityIndex)"
				>
					<v-icon size="24px" color="#fb3640">mdi-delete</v-icon>
					{{ $t('text.deleteTimeframe') }}
				</v-btn>
				-->
			</div>
			<button id="btnAddAvailabilityRow" class="addPanel" elevation="0"
				style="height:40px;font-size:12pt;margin-right:10px;margin-top:15px;" @click="addAvailabilityRow()"
			>
				<v-icon size="24px">mdi-plus</v-icon>
				{{ $t('text.addTimeframe') }}
			</button>

			<!-- Overall Manual Contingents Overview -->
			<div v-if="manualValidities.length > 0" style="margin-top: 20px; padding-top: 20px; border-top: 1px solid #e0e0e0;">
				<div style="margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between;">
					<div>
						<h4 style="margin: 0; color: #333; font-size: 16px; font-weight: 600;">
							{{ $t('text.allManualContingentsOverview') }}
						</h4>
						<p style="margin: 5px 0 0 0; font-size: 14px; color: #666;">
							{{ $t('text.allManualContingentsOverviewDescription') }}
						</p>
					</div>
					<v-btn
						size="small"
						variant="text"
						@click="showOverviewSection = !showOverviewSection"
						style="min-width: auto; padding: 8px;"
					>
						<v-icon size="16" :color="showOverviewSection ? '#1976d2' : '#666'">
							{{ showOverviewSection ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
						</v-icon>
						<span style="margin-left: 4px; font-size: 14px;">
							{{ showOverviewSection ? $t('text.hide') : $t('text.show') }}
						</span>
					</v-btn>
				</div>
				<IfFeatureFlag flag="FEATURE_MANUAL_CONTINGENTS_WARNING">
					<ManualContingentsWarning 
						:manual-validities="getAllManualValiditiesFormatted()"
						expanded-key="overview"
						:closable="false"
						:space-id="model?.fields?.ticket?.de?.fields?.ticketOptions?.de?.[0]?.fields?.spaceId?.de"
						@navigate-to-date="navigateToDateInCalendar"
						@validity-deleted="onValidityDeleted"
						v-if="showOverviewSection"
					/>
				</IfFeatureFlag>
			</div>
		</div>

		<ContingentCalendarNew v-if="featureEnabled('contingent-monitor')" ref="contingentCalendar"
			:availabilities="model.fields.productAvailabilities.de" :product="product"
			style="margin-top: 20px;"
			@contingent-updated="onContingentUpdatedFromCalendar"
		/>

		<Dialog ref="updateRequestOnlyDialog"
			:title="$t('text.updateRequestOnly')"
			:confirmLabel="$t('text.confirm')"
			:cancelLabel="$t('text.cancel')"
			:cancelHandler="onUpdateRequestOnlyDialogCancel"
			width="540px"
			height="350px"
			dataCy="update-request-only-dialog"
		>
			<template #content>
				<div class="update-request-only-container" style="display: flex; gap: 20px; align-items: center;">
					<v-icon color="warning" size="96px">mdi-alert</v-icon>
					<div class="update-request-only-text">
						<h4 class="update-request-only-title">{{ $t('text.updateRequestOnlyWarningTitle') }}</h4>
						<p class="update-request-only-desc">{{ $t('text.updateRequestOnlyWarningDesc') }}</p>
					</div>
				</div>
			</template>
		</Dialog>
	</Disclosure>
</template>

<script>
import AvailabilityExceptionSelector from './AvailabilityExceptionSelector.vue'
import Disclosure from '@/components/common/Disclosure.vue'
import Common from "@/mixins/Common.vue"
import isEqual from 'lodash/isEqual'
import DatePicker from "@/components/datePicker/DatePicker.vue"
import ContingentCalendarNew from './contingentMonitor/ContingentCalendarNew.vue'
import IfFeatureFlag from '../../components/ifFeatureFlag/IfFeatureFlag.vue'
import Dialog from '@/components/common/Dialog.vue'
import Toast from '@/mixins/Toast.vue'
import ManualContingentsWarning from './ManualContingentsWarning.vue'
import NumberField from '../fields/NumberField.vue'
import { Features } from '../../../../shared/features'

export default {
	name: 'Availability',
	components: { AvailabilityExceptionSelector, Disclosure, DatePicker, ContingentCalendarNew, IfFeatureFlag, Dialog, ManualContingentsWarning, NumberField },
	mixins: [ Common, Toast ],
	props: {
		product: Object,
		updateModel: Boolean,
		disabled: Boolean,
		productIsExternal: Boolean,
		csError: Object,
		csDeletedValidityError: Boolean,
	},
	data: () => ({
		model: {},
		initData: {},
		isNewProduct: false,
		fromDate: null,
		toDate: null,
		counter: 0,
		contingentProblem: false,
		originalAvailabilities: [],
		cbMonths: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
		cbWeeks: [0, 1, 2, 3],
		cbDays: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31],
		cbHours: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
		sectionMessage: {
			error: false,
			warning: false,
			message: ''
		},
		daysOfWeek: [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ],
		timeSlotProblem: false,
		productAvailabilitiesWatcherCount: 0,
		manualValidities: [],
		expandedContingents: new Set(), // Track which contingents are expanded to show manual overrides
		showOverviewSection: true, // Track if overview section is shown (default: true)
		tempMaxContingents: null, // Store previous max contingent values when requestOnly is changed
		contingentThresholdFieldDefinition: { validations: [] },
	}),
	watch: {
		updateModel() {
			this.model = this.valueToModel(this.product)
			this.normalizeRequestOnlyField()
			this.normalizeContingentThresholdField()
			this.setAvailabilityDetails()
			this.setInitData()
			
			// When the product model is updated, refresh manual validities
			if (this.featureEnabled('contingent-monitor')) {
				this.refreshManualValidities()
				// Reset overview section visibility when product changes
				this.showOverviewSection = true
			}
		},
		productAvailabilities: {
			deep: true,
			handler() {
				if (this.productAvailabilitiesWatcherCount < 2)
					this.productAvailabilitiesWatcherCount++
				if (!this.productAvailabilities?.length) {
					this.$emit('setRefactoredProductAvailabilities', [])
					return
				}
				const availabilities = this.productAvailabilities
					.map(({ sys, fields }) => {
						const validFromDate = fields?.validFromDate?.de
						const validToDate = fields?.validToDate?.de

						if (!validFromDate || !validToDate) return null

						const timeSlots = fields?.productContingents?.de
							?.filter(x => x.fields?.timeSlot?.de && x.fields.timeSlot.de.split(':').length === 2)
							?.map(x => x.fields.timeSlot.de)

						if (this.productAvailabilitiesWatcherCount === 2) {
							const initAvailability = this.initData?.productAvailabilities?.de?.find(x => x.sys?.id === sys.id)
							if (initAvailability && !initAvailability?.fields?.exceptions?.de?.length && fields?.exceptions?.de?.length) {
								initAvailability.fields.exceptions = JSON.parse(JSON.stringify(fields.exceptions))
							}	
						}
						
						return { validFromDate, validToDate, timeSlots, exceptions: fields?.exceptions?.de }
					})
					.filter(Boolean)
				this.$emit('setRefactoredProductAvailabilities', availabilities)
			}
		},
	},
	computed: {
		productAvailabilities() {
			return this.model?.fields?.productAvailabilities?.de
		},
		productWasOnceApproved() {
			let clientAssignment = this.model?.fields?.clientAssignments?.de.find(assignment => assignment.fields?.client?.de?.sys?.id === this.$store.state.selectedClient.sys.id)
			return !(!clientAssignment?.fields?.approvalDate || clientAssignment?.fields?.approvalDate?.de === "")
		},
		formatCSErrors() {
			if (!this.csError) return ''

			let formattedErrors = ''
			let errors = this.csError?.messageForEndUser[this.selectedLocale].split("\n")

			for (const error of errors) {
				if (error.length) {
					formattedErrors += `${error}<br/>`
				}
			}

			return formattedErrors
		},
		serviceWithTicket() {
			return this.model?.fields?.serviceType?.de?.sys?.id === 'SERVICE-TYPE-EVENT-1' || this.$store.state.selectedServiceType?.sys?.id === 'SERVICE-TYPE-EVENT-1'
		},
		requestOnlyService() {
			return this.getRequestOnlyValue()
		},
		showContingentThreshold() {
			return this.featureEnabled(Features.CONTINGENT_THRESHOLD) && this.serviceWithTicket && !this.requestOnlyService
		},
		earliestValidFromDate() {
			const availabilities = this.model?.fields?.productAvailabilities?.de || []
			if (!availabilities.length) return null

			const validDates = availabilities
				.map(availability => availability.fields?.validFromDate?.de)
				.filter(date => date) // Filter out undefined/null dates

			if (!validDates.length) return null

			return validDates.reduce((earliest, current) => {
				return current < earliest ? current : earliest
			}, validDates[0])
		},
		groupedManualValidities() {
			const grouped = {}
			
			for (const validity of this.manualValidities) {
				const dateKey = this.getLocalDateStringFromValidity(validity.startDateTime)
				const timeKey = `${validity.hour.toString().padStart(2, '0')}:${validity.minute.toString().padStart(2, '0')}`
				
				if (!grouped[dateKey]) {
					grouped[dateKey] = {}
				}
				if (!grouped[dateKey][timeKey]) {
					grouped[dateKey][timeKey] = []
				}
				
				grouped[dateKey][timeKey].push({
					...validity,
					displayTime: timeKey,
					displayDate: dateKey
				})
			}
			return grouped
		},
	},
	methods: {
		valueToModel(v) {
			return JSON.parse(JSON.stringify(v ?? {}))
		},
		getRequestOnlyValue(requestOnly = this.model?.fields?.requestOnly) {
			return requestOnly?.de === true || requestOnly === true
		},
		normalizeRequestOnlyField(targetModel = this.model) {
			if (!targetModel?.fields) return
			targetModel.fields.requestOnly = { de: this.getRequestOnlyValue(targetModel.fields.requestOnly) }
		},
		getContingentThresholdValue(contingentThreshold = this.model?.fields?.contingentThreshold) {
			const raw = contingentThreshold !== null && typeof contingentThreshold === 'object' ? contingentThreshold.de : contingentThreshold
			if (raw === null || raw === undefined || raw === '') return null
			const value = Number(raw)
			return Number.isInteger(value) && value > 0 ? value : null
		},
		normalizeContingentThresholdField(targetModel = this.model) {
			if (!targetModel?.fields || !this.serviceWithTicket) return
			targetModel.fields.contingentThreshold = { de: this.getContingentThresholdValue(targetModel.fields.contingentThreshold) }
		},
		setInitData() {
			const initModel = JSON.parse(JSON.stringify(this.model))
			this.normalizeRequestOnlyField(initModel)
			this.initData = {
				requestOnly: { de: this.getRequestOnlyValue(initModel?.fields?.requestOnly) },
			}

			if (this.serviceWithTicket) {
				this.initData.contingentThreshold = { de: this.getContingentThresholdValue(initModel?.fields?.contingentThreshold) }
			}

			if (this.hasField('productAvailabilities')) {
				this.initData.productAvailabilities = initModel.fields.productAvailabilities
			}

			this.$refs.contingentCalendar?.load?.()
		},
		sendData() {
			this.normalizeRequestOnlyField()
			let data = {
				requestOnly: { de: this.getRequestOnlyValue() },
			}

			if (this.serviceWithTicket) {
				// request only products have no real contingent to display
				data.contingentThreshold = { de: this.requestOnlyService ? null : this.getContingentThresholdValue() }
			}

			if (this.hasField('productAvailabilities')) {
				data.productAvailabilities = this.model.fields.productAvailabilities
			}

			if (this.hasField('productContingents')) {
				if (data.productAvailabilities?.de?.length) {
					for (const availability of data.productAvailabilities.de) {
						if (availability.fields.productContingents?.de?.length) {
							for (let contingent of availability.fields.productContingents.de) {
								contingent.fields.minimumContingent.de = parseInt(contingent.fields.minimumContingent.de)
								contingent.fields.maximumContingent.de = parseInt(contingent.fields.maximumContingent.de)

								//MYS-3346: ContingentService - persist original timeslot
								const initAvailability = this.initData.productAvailabilities.de.find(pa => pa.sys.id === availability.sys.id)

								if (initAvailability) {
									const initContingent = initAvailability.fields.productContingents.de.find(pc => pc.sys.id === contingent.sys.id)
									if (initContingent && initContingent.fields.timeSlot.de !== contingent.fields.timeSlot.de) {
										contingent.addl = { timeSlot: initContingent.fields.timeSlot.de?.length ? initContingent.fields.timeSlot.de : '00:00' }
									}
								}
							}
						}
					}
				}
			}

			data.changed = !!(!isEqual(data, this.initData) || this.csError);

			return data
		},
		validateAllFields() {
			let allFieldsAreValid = true
			this.resetSectionError(this.sectionMessage)

			if (this.productIsExternal) return allFieldsAreValid

			if (this.hasField('productAvailabilities')) {
				let haveAvailabilities = false
				let haveTimeFrame = false
				let haveContingents = false
				let overlappingAvaliabilities = false

				if (this.model.fields.productAvailabilities.de.length > 0) {
					haveAvailabilities = true
					haveTimeFrame = true
					haveContingents = true

					for (const availability of this.model.fields.productAvailabilities.de) {
						if (!availability || !availability.fields) {
							haveTimeFrame = false
							continue;
						}
						if (!availability.fields.validFromDate?.de || !availability.fields.validToDate?.de ||
							availability.fields.validFromDate.de.length === 0 || availability.fields.validToDate.de.length === 0) {
							haveTimeFrame = false
						}
						if (this.hasField('productContingents')) {
							if (!availability.fields.productContingents?.de?.length) {
								haveContingents = false
								continue
							}
							for (const contingent of availability.fields.productContingents.de) {
								if (!contingent.fields.maximumContingent?.de || contingent.fields.maximumContingent.de === '0') {
									haveContingents = false
								}
							}
						}
						if (availability.fields.validFromDate?.de && availability.fields.validToDate?.de) {
							const fromDate = new Date(availability.fields.validFromDate.de)
							const toDate = new Date(availability.fields.validToDate.de)
							if (fromDate > toDate) {
								this.setSectionError(this.sectionMessage, this.$t('text.invalidTimeFrameError'))
							}

							// check if the dates of the current availability overlap with the dates of any other availability
							for (const otherAvailability of this.model.fields.productAvailabilities.de) {
								if (availability.sys.id === otherAvailability.sys.id) continue
								if (!otherAvailability.fields.validFromDate?.de || !otherAvailability.fields.validToDate?.de) continue
								const otherFromDate = new Date(otherAvailability.fields.validFromDate.de)
								const otherToDate = new Date(otherAvailability.fields.validToDate.de)
								if (fromDate <= otherToDate && otherFromDate <= toDate) {
									const otherContingents = otherAvailability.fields.productContingents?.de
									const currentContingents = availability.fields.productContingents?.de
									if (otherContingents && currentContingents) {
										const overlappingTimeslotFound = otherContingents.some(otherContingent => {
											return currentContingents.some(currentContingent => {
												return otherContingent.fields.timeSlot.de === currentContingent.fields.timeSlot.de
											})
										})
										overlappingAvaliabilities = overlappingTimeslotFound
									}
								}
							}
						}
					}
				}
				if (!haveAvailabilities || !haveContingents || !haveTimeFrame) {
					allFieldsAreValid = false
					this.setSectionError(this.sectionMessage, this.$t('text.missingFieldsError'))
				} else if (this.contingentProblem) {
					allFieldsAreValid = false
					this.setSectionError(this.sectionMessage, this.$t('text.contingentError'))
				} else if (this.timeSlotProblem) {
					allFieldsAreValid = false
					this.setSectionError(this.sectionMessage, this.$t('text.contingentTimeSlotError'))
				} else if (overlappingAvaliabilities) {
					allFieldsAreValid = false
					this.setSectionError(this.sectionMessage, this.$t('text.overlappingTimeFrameError'))
				}
			}

			return allFieldsAreValid
		},
		// TODO: unused
		calcValidFromDateMin(availabilityIndex) {
			if (!this.isNewProduct && this.productWasOnceApproved && this.originalAvailabilities?.[availabilityIndex]?.fields?.validFromDate?.de?.length > 0) {
				return this.originalAvailabilities[availabilityIndex].fields.validToDate.de
			}

			return this.model.fields?.productAvailabilities?.de?.[availabilityIndex]?.fields?.validFromDate?.de
		},
		// TODO: unused
		validFromDateDisabled(availabilityIndex) {
			return !this.isNewProduct && this.productWasOnceApproved &&
				!this.checkSavedStatusForAllClientAssignments() &&
				this.originalAvailabilities?.[availabilityIndex]?.sys?.id.indexOf('id_') < 0 &&
				this.originalAvailabilities?.[availabilityIndex]?.fields?.validFromDate?.de?.length > 0
		},
		// TODO: unused
		calcValidFromDateMax(availabilityIndex) {
			// the maximum of the validFrom date should be the validTo date value for that timeframe
			return this.model.fields.productAvailabilities.de[availabilityIndex]?.fields?.validToDate?.de
		},
		// TODO: unused if validFromDateDisabled is unused
		checkSavedStatusForAllClientAssignments() {
			for (const clientAssignment of this.model.fields.clientAssignments.de) {
				if (clientAssignment.fields?.status?.de !== 'saved') {
					return false
				}
			}
			return true
		},
		hasField(fieldName) {
			return this.model.fields?.serviceType?.de?.fields?.template?.de?.availabilityInfo?.[fieldName]
		},
		getNewAvailability(counter) {
			return {
				sys: { id: 'id_av_' + counter },
				fields: {
					validFromDate: { de: '' },
					validToDate: { de: '' },
					productContingents: {
						de: [this.getNewContingent(this.counter)],
					},
					exceptions: {
						de: this.daysOfWeek,
						type: '',
					}
				}
			}
		},
		getNewContingent(counter) {
			return {
				sys: { id: 'id_con_' + counter },
				fields: {
					minimumContingent: { de: 1 },
					maximumContingent: { de: this.getRequestOnlyValue() === true ? 999999 : 1 },
					timeSlot: { de: '' },
				}
			}
		},
		addAvailabilityRow() {
			this.counter++;
			this.model.fields.productAvailabilities.de.push(this.getNewAvailability(this.counter))

			// update originalAvailabilities
			this.originalAvailabilities = JSON.parse(JSON.stringify(this.model.fields.productAvailabilities.de))
		},
		removeAvailabilityRow(availabilityIndex) {
			if (this.model.fields.productAvailabilities.de.length > 1) {
				this.model.fields.productAvailabilities.de.splice(availabilityIndex, 1)
			} else {
				this.counter++;
				this.model.fields.productAvailabilities.de = [this.getNewAvailability(this.counter)]
			}
			// update originalAvailabilities
			this.originalAvailabilities = JSON.parse(JSON.stringify(this.model.fields.productAvailabilities.de))
		},
		addContingentRow(availabilityIndex) {
			this.model.fields.productAvailabilities.de[availabilityIndex].fields.productContingents.de.push(this.getNewContingent(Math.random()))
		},
		removeContingentRow(availabilityIndex, contingentIndex) {
			this.model.fields.productAvailabilities.de[availabilityIndex].fields.productContingents.de.splice(contingentIndex, 1)
		},
		fixTime(t) {
			if (t.length == 0) return ''
			if (t.length == 1) return `0${t}:00`
			if (t.length == 2) return `${t}:00`
			if (t.length == 3) return `${t}00`
			if (t.length == 4) return `${t}0`
			return t
		},
		checkNumber(event) {
			if (event.target.value < 0) {
				event.target.value = 1
			}
		},
		validFromToError(availability, prop) {
			let error
			if (!availability.fields[prop]?.de?.length) {
				error = this.$t('text.missingFieldsError')
			} else {
				//MYS-3346: ContingentService - Check if it's the from or the to date that has changed to exclude an existing reservation
				if (this.csError?.data?.availabilityId === availability.sys.id) {
					if (prop === 'validFromDate') {
						if (this.csError.data.errorItems.find(error => error.startDateTime.split(' ')[0] < availability.fields.validFromDate.de)) {
							error = 'Contingent Service Error'
						}
					} else {
						if (this.csError.data.errorItems.find(error => error.startDateTime.split(' ')[0] > availability.fields.validToDate.de)) {
							error = 'Contingent Service Error'
						}
					}
				}
			}
			return error
		},
		contingentError(contingent) {
			let error

			if (!contingent.fields.maximumContingent?.de || contingent.fields.maximumContingent.de === 0) {
				error = this.$t('text.missingFieldsError')
			} else {
				//MYS-3346: ContingentService - Check if there is an error where the contingent amount is less than the number of reservations
				if (this.csError?.data?.productContingentId === contingent.sys.id) {
					if (this.csError.data.errorItems.find(error => error.reservationCount > contingent.fields.maximumContingent.de)) {
						error = 'Contingent Service Error'
					}
				}
			}
			return error
		},
		timeSlotError(contingent) {
			let error
			this.timeSlotProblem = false

			// loop over every availability and every contingent and make sure that the time slots are different in every contingent of that availability. 
			// We do this because if we only check the current contingent, the user could change the time slot of the current contingent to be the same and then change the time slot 
			// of a contingent in another availability to be different from the other contingent(s) there and the error would not be shown for the previous availability.
			for (let i = 0; i < this.model.fields.productAvailabilities.de.length; i++) {
				let availability = this.model.fields.productAvailabilities.de[i]
				for (let j = 0; j < availability.fields.productContingents.de.length; j++) {
					let contingent = availability.fields.productContingents.de[j]

					for (let k = 0; k < availability.fields.productContingents.de.length; k++) {
						let otherContingent = availability.fields.productContingents.de[k]
						if (contingent.fields.timeSlot.de === otherContingent.fields.timeSlot.de && j !== k) {
							error = this.$t('text.contingentTimeSlotError')
							this.timeSlotProblem = true
						}
					}
				}
			}

			if (!error) {
				//MYS-3346: ContingentService - Check if there is an error with the time slot
				if (this.csError?.data?.productContingentId === contingent.sys.id) {
					if (this.csError.data.errorItems.find(error => error.startDateTime.split(' ')[1] !== contingent.fields.timeSlot.de)) {
						error = 'Contingent Service Error'
					}
				}
			}

			return error
		},
		setAvailabilityDetails() {
			for (const availability of this.model.fields.productAvailabilities.de) {
				if (!availability.fields.exceptions) {
					availability.fields['exceptions'] = { de: this.daysOfWeek }
				}

				if (!availability.fields.productContingents) {
					availability.fields["productContingents"] = { de: [this.getNewContingent(1)] }
				}
			}
		},
		async getManualValidities() {
			const spaceId = this.model?.fields?.ticket?.de?.fields?.ticketOptions?.de?.[0]?.fields?.spaceId?.de
			if (!spaceId) {
				return
			}
			
			try {
				const response = await this.$httpGet(`/manual-validities/${spaceId}/${this.earliestValidFromDate}`)
				this.manualValidities = response || []
			} catch (error) {
				console.error('Error fetching manual validities:', error)
				this.manualValidities = []
			}
		},
		toggleContingentExpansion(availabilityIndex, contingentIndex) {
			const key = `${availabilityIndex}-${contingentIndex}`
			// Only show the warning on focus if it's not already expanded and there are manual contingents
			const availability = this.model.fields.productAvailabilities.de[availabilityIndex]
			const contingent = availability.fields.productContingents.de[contingentIndex]
			const hasManualContingents = this.getManualValiditiesForContingent(availability, contingent).length > 0
			
			if (!this.expandedContingents.has(key) && hasManualContingents) {
				this.expandedContingents.add(key)
				// Force reactivity update
				this.expandedContingents = new Set(this.expandedContingents)
			}
		},
		hideContingentExpansion(availabilityIndex, contingentIndex) {
			const key = `${availabilityIndex}-${contingentIndex}`
			this.expandedContingents.delete(key)
			// Force reactivity update
			this.expandedContingents = new Set(this.expandedContingents)
		},
		isContingentExpanded(availabilityIndex, contingentIndex) {
			return this.expandedContingents.has(`${availabilityIndex}-${contingentIndex}`)
		},
		getManualValiditiesForContingent(availability, contingent) {
			const timeSlot = contingent.fields.timeSlot.de
			const validFromDate = availability.fields.validFromDate.de
			const validToDate = availability.fields.validToDate.de
			
			if (!validFromDate || !validToDate) {
				return []
			}
			
			const result = []
			
			for (const validity of this.manualValidities) {
				// Create time string from hour and minute
				const validityTimeSlot = `${validity.hour.toString().padStart(2, '0')}:${validity.minute.toString().padStart(2, '0')}`
				const localDateString = this.getLocalDateStringFromValidity(validity.startDateTime)
				const inDateRange = localDateString >= validFromDate && localDateString <= validToDate
				// If no timeSlot is set, default to '00:00' and match only that specific timeslot
				const effectiveTimeSlot = timeSlot || '00:00'
				const timeMatch = effectiveTimeSlot === validityTimeSlot
				
				// Check if validity is within date range and matches the specific timeslot
				if (inDateRange && timeMatch) {
					result.push({
						...validity,
						formattedDate: this.formatDate(localDateString),
						formattedTime: validityTimeSlot === '00:00' ? this.$t('text.day') : validityTimeSlot,
						displayDate: localDateString
					})
				}
			}
			
			return result.sort((a, b) => a.displayDate.localeCompare(b.displayDate))
		},
		formatDate(dateString) {
			const [year, month, day] = dateString.split('-').map(Number)
			const date = new Date(year, month - 1, day)
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric'
			})
		},
		getLocalDateStringFromValidity(startDateTime) {
			const validityDate = new Date(`${startDateTime}Z`)
			return `${validityDate.getFullYear()}-${(validityDate.getMonth() + 1).toString().padStart(2, '0')}-${validityDate.getDate().toString().padStart(2, '0')}`
		},
		getAllManualValiditiesFormatted() {
			// Return all manual validities with formatted data for the overview
			const result = []
			
			for (const validity of this.manualValidities) {
				// Create time string from hour and minute
				const validityTimeSlot = `${validity.hour.toString().padStart(2, '0')}:${validity.minute.toString().padStart(2, '0')}`
				const localDateString = this.getLocalDateStringFromValidity(validity.startDateTime)
				
				result.push({
					...validity,
					formattedDate: this.formatDate(localDateString),
					formattedTime: validityTimeSlot === '00:00' ? this.$t('text.day') : validityTimeSlot,
					displayDate: localDateString
				})
			}
			
			// Sort by date, then by time
			return result.sort((a, b) => {
				const dateCompare = a.displayDate.localeCompare(b.displayDate)
				if (dateCompare !== 0) return dateCompare
				// For time sorting, put 'day' (00:00) first, then sort other times
				if (a.formattedTime === this.$t('text.day') && b.formattedTime !== this.$t('text.day')) return -1
				if (a.formattedTime !== this.$t('text.day') && b.formattedTime === this.$t('text.day')) return 1
				return a.formattedTime.localeCompare(b.formattedTime)
			})
		},
		refreshContingentCalendar() {
			// Refresh the contingent calendar component
			if (this.$refs.contingentCalendar && this.$refs.contingentCalendar.load) {
				this.$refs.contingentCalendar.load()
			}
		},
		async refreshManualValidities() {
			// Refresh manual validities data
			await this.getManualValidities()
		},
		async refreshAllContingentData() {
			// Refresh both manual validities and calendar
			await this.refreshManualValidities()
			this.refreshContingentCalendar()
		},
		onContingentUpdatedFromCalendar(eventData) {
			// Called when contingent is updated from the calendar component
			// Refresh manual validities to show any new manual overrides
			this.refreshManualValidities()
		},
		navigateToDateInCalendar(dateString) {
			// Navigate to specific date in the contingent calendar
			if (this.$refs.contingentCalendar && this.$refs.contingentCalendar.navigateToDate) {
				this.$refs.contingentCalendar.navigateToDate(dateString)
				// Scroll to calendar after a short delay to ensure navigation completed
				this.$nextTick(() => {
					setTimeout(() => {
						this.$refs.contingentCalendar.$el.scrollIntoView({ 
							behavior: 'smooth', 
							block: 'start' 
						})
					}, 300)
				})
			}
		},
		onValidityDeleted(validity) {
			// Remove the deleted validity from local data
			const index = this.manualValidities.findIndex(mv => mv.validityId === validity.validityId)
			if (index !== -1) {
				this.manualValidities.splice(index, 1)
			}
			
			// Refresh the contingent calendar to reflect the deletion
			this.refreshContingentCalendar()
		},
		onRequestOnlyChanged(event) {
			const eventValue = event?.target?.checked
			const nextRequestOnlyValue = typeof eventValue === 'boolean'
				? eventValue
				: !this.getRequestOnlyValue()
			this.model.fields.requestOnly = { de: nextRequestOnlyValue }

			// Show dialog when requestOnly is enabled (changed to true)
			if (nextRequestOnlyValue === true) {
			// Store the previous values so that they can be restored if the user cancels
				this.tempMaxContingents = this.model.fields.productAvailabilities.de.map(availability => {
					return {
						sys: { ...availability.sys },
						fields: {
							productContingents: availability.fields.productContingents.de.map(contingent => ({
								sys: { ...contingent.sys },
								fields: { maximumContingent: contingent.fields.maximumContingent.de }
							}))
						}
					}
				})

				// Update all maximum contingents of all availabilities to 999999
				for (const availability of this.model.fields.productAvailabilities.de) {
					for (const contingent of availability.fields.productContingents.de) {
						contingent.fields.maximumContingent.de = 999999
					}
				}

				this.$refs.updateRequestOnlyDialog.show = true
			} else if (this.tempMaxContingents) {
				this.restoreTempMaxContingents()
			} else if (this.product.fields.requestOnly?.de === true) {
				// requestOnly is changed to false but was true before, set contingent back to default 6 since we cannot restore previous values
				for (const availability of this.model.fields.productAvailabilities.de) {
					for (const contingent of availability.fields.productContingents.de) {
						contingent.fields.maximumContingent.de = 6
					}
				}
			}
		},
		onUpdateRequestOnlyDialogCancel() {
			// Set requestOnly back to false if user cancels
			this.model.fields.requestOnly = { de: false }

			// Restore previous maximum contingent values from tempMaxContingents
			if (this.tempMaxContingents) {
				this.restoreTempMaxContingents()
			}
		},
		restoreTempMaxContingents() {
			for (const tempAvailability of this.tempMaxContingents) {
				const availability = this.model.fields.productAvailabilities.de.find(av => av.sys.id === tempAvailability.sys.id)
				if (availability) {
					for (const tempContingent of tempAvailability.fields.productContingents) {
						const contingent = availability.fields.productContingents.de.find(c => c.sys.id === tempContingent.sys.id)
						if (contingent) {
							contingent.fields.maximumContingent.de = tempContingent.fields.maximumContingent
						}
					}
				}
			}
			// Clear tempMaxContingents after restoring
			this.tempMaxContingents = null
		}
	},
	created() {
		this.model = this.valueToModel(this.product)
		this.normalizeRequestOnlyField()
		this.normalizeContingentThresholdField()
		this.setAvailabilityDetails()
	},
	mounted() {
		this.isNewProduct = this.product.sys.id === ''
		// copy product availabilities so that we have a reference of the original data
		this.originalAvailabilities = JSON.parse(JSON.stringify(this.model.fields.productAvailabilities.de))

		// MYS-4662: the backend was catering for this before, but IMO the frontend should be able to initialize availabilities if they are not present.
		if (!this.model.fields.productAvailabilities?.de?.length) {
			this.model.fields.productAvailabilities = { de: [this.getNewAvailability(1)] }
		} else {
			this.model.fields.productAvailabilities.de.forEach((availability, index) => {
				if (!availability.fields.productContingents?.de?.length) {
					availability.fields.productContingents = { de: [this.getNewContingent(index)] }
				}
			})
		}
		this.setInitData()
		
		if (this.featureEnabled('contingent-monitor')) {
			this.getManualValidities()
		}
	},
}
</script>
<style scoped>
.contingentThreshold .Field { --col-line: #cfd9e0; }
	h4 {
		color: #000000;
		font-size: 14pt;
		text-align: left;
		font-weight: bold;
	}

	.overflowButton {
		background: linear-gradient(0deg, #efefef 0%, #ffffff 100%) !important;
		display: flex !important;
		justify-content: space-around !important;
		align-items: center !important;
		border-radius: 5px !important;
		font-family: 'Inter', sans-serif;
		font-size: 12pt !important;
		text-transform: none !important;
		letter-spacing: 0 !important;
	}

	.v-overflow-btn.v-input--is-focused .v-input__slot,
	.v-overflow-btn.v-combobox--is-menu-active .v-input__slot {
		box-shadow: none !important;
		border: solid 1px rgba(0, 0, 0, 0.1) !important;
	}

	.disabled {
		opacity: 0.5;
		pointer-events: none;
	}

	.error-text {
		color: var(--error-color);
	}

	.error-container {
		background-color: #f34545;
		border-radius: 10px;
		padding: 10px;
		color: #ffffff
	}

	.error-container #errors {
		color: #ffffff;
		font-size: 14pt;
	}

	div.col2 {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	div.col2>* {
		width: 50%;
	}

	div.col4 {
		display: flex;
		gap: 10px;
		align-items: center;
	}

	div.col4>* {
		width: 25%;
	}

	.red-text {
		color: #f34545;
	}

	.contingent-row {
		margin-bottom: 10px;
	}


</style>